import React from "react";
import { Button } from "../../../components/ui";
import { formatCreatedOn } from "../../brand/planFormat";
import Milestone from "./Milestone";
import { formatMoney } from "../pricing";
import {
  TRIAL_DAYS,
  TRIAL_REMINDER_DAYS,
  trialBillingOf,
  trialReminderOf,
} from "../trialState";
import type { PriceBreakdown, SubscriptionPlan } from "../types/billing.types";

/** One line of the breakdown: what it is on the left, what it comes to on the
 *  right. Tabular figures so the column of rupees lines up. */
const Line: React.FC<{ label: React.ReactNode; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-baseline justify-between gap-4">
    <span className="text-[13px] 3xl:text-[14px] text-ink-muted">{label}</span>
    <span className="tabular-nums text-[13px] 3xl:text-[14px] font-medium text-ink">
      {value}
    </span>
  </div>
);

interface TrialSummaryProps {
  plan: SubscriptionPlan;
  /** What the plan will cost when the trial ends — off `POST /billing/quote/`,
   *  the same answer the paid checkout prints. Nothing is charged today. */
  price: PriceBreakdown;
  /** The last day the fourteen cover — day fourteen, today being day one.
   *  Passed in rather than worked out here so the summary and the confirmation
   *  quote one date; see `trialState`. */
  endsOn: Date;
  onStart: () => void;
  /** The details are with the server — the button spins and stays pressed. */
  starting?: boolean;
}

/**
 * What starting the trial signs somebody up to, beside the form that says who
 * it will be billed to.
 *
 * The paid checkout's `OrderSummary` with one figure moved: the plan, its
 * subtotal and its tax are all still shown, because they are what will be
 * charged in a fortnight and hiding them until then is how a free trial turns
 * into a surprise. What is different is the line that matters today, which is
 * **₹0** — and the timeline under it, which is three stops rather than two: the
 * trial starting, the mail that warns it is ending, and the day the first
 * charge falls.
 *
 * Every figure is the quote's. Nothing on this card is arithmetic done in the
 * browser except the two dates, which have nowhere else to come from — there is
 * no trial endpoint; see `trialState`.
 */
const TrialSummary: React.FC<TrialSummaryProps> = ({
  plan,
  price,
  endsOn,
  onStart,
  starting = false,
}) => {
  // What the charge will read as, in the words of the cycle being started —
  // a month of the monthly plan, a year of the yearly one. `price.taxableAmount`
  // is that whole term, which is why the label has to follow the cycle rather
  // than always saying "month".
  const term = plan.id === "yearly" ? "year" : "month";

  // The other two dates of the fortnight, off the last day it covers. Day one
  // is today — the way a recharge counts validity — so `endsOn` is the
  // fourteenth day and the reminder falls on the eleventh; see `trialState`.
  const remindsOn = trialReminderOf(endsOn);
  const chargedOn = trialBillingOf(endsOn);

  return (
    <section
      aria-labelledby="trial-summary-heading"
      className="rounded-md border border-[#5BD3E1] bg-[#F8FEFF] p-5 3xl:p-6"
    >
      <h2
        id="trial-summary-heading"
        className="text-[14px] 3xl:text-[15px] font-medium uppercase tracking-[0.08em] text-ink"
      >
        Plan details
      </h2>

      <div className="mt-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[15px] 3xl:text-[16px] font-semibold text-ink">
            {plan.productName}
          </p>
          <p className="mt-0.5 text-[12px] 3xl:text-[13px] text-ink-muted">
            {plan.cycleLabel} plan
          </p>
        </div>
        <span className="tabular-nums text-[15px] 3xl:text-[16px] font-medium text-ink">
          {formatMoney(price.taxableAmount)}
        </span>
      </div>

      {/* No subtotal line. It would restate the figure printed against the plan
          two rows above it, which is what the subtotal *is* here — there is no
          coupon and nothing else in the order to sum. What is left is the two
          lines that are not already on screen: the tax, and what they come to
          together. */}
      <div className="mt-4 flex flex-col gap-2.5">
        <Line
          label={`GST ${Math.round(price.taxRate * 100)}%`}
          value={formatMoney(price.tax)}
        />
        {/* Darker than the line above it: this is the figure that will actually
            be charged when the trial ends, and the tax above is a component of
            it rather than a second amount. */}
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-[14px] 3xl:text-[15px] font-medium text-ink">
            Total amount
          </span>
          <span className="tabular-nums text-[14px] 3xl:text-[15px] font-semibold text-ink">
            {formatMoney(price.total)}
          </span>
        </div>
      </div>

      {/* The one figure that is about today, and it is nothing. It sits where
          the checkout puts its total, in the same weight, because it is the
          same line of the same question — "what am I paying now" — and the
          answer being ₹0 is the whole offer. Below the rule that closes the
          figures above, which are all about the day the trial ends. */}
      <div className="mt-4 flex items-start justify-between gap-4 border-t border-line pt-4">
        <div>
          <p className="text-[16px] 3xl:text-[18px] font-semibold text-ink">
            Payable amount
          </p>
          <p className="mt-0.5 text-[12px] 3xl:text-[13px] text-ink-muted">
            {TRIAL_DAYS}-day free trial &middot; No payment required
          </p>
        </div>
        <span className="tabular-nums text-[21px] 3xl:text-[24px] font-bold text-ink">
          {formatMoney(0)}
        </span>
      </div>

      {/* Three stops, and the last one is the only one that costs anything.
          The middle is a promise about a mail rather than about money, and it
          is here because a trial that ends without warning is the complaint
          every free trial gets. */}
      <ul className="mt-5">
        <Milestone
          done
          title="Free trial starts from today"
          detail={`Full access to ${plan.productName}`}
          // What pressing the button gets you, said as a state rather than as
          // an event — violet, the colour this feature already gives an aside
          // beside a figure; see `PlanCard`'s price note.
          tag={
            <span className="rounded-full border border-[#DCCFFA] bg-[#F4EEFF] px-2.5 py-0.5 text-[11px] 3xl:text-[12px] font-medium text-[#6234CC]">
              {plan.productName} unlocked
            </span>
          }
        />
        <Milestone
          title={`${formatCreatedOn(remindsOn)} · trial ending reminder`}
          detail={`We'll email you ${TRIAL_REMINDER_DAYS} days before it ends.`}
        />
        {/* The last day and the day of the charge, said as two dates rather
            than one. They are a day apart — the trial covers its fourteenth
            day in full, and the first payment falls the morning after — and a
            single date doing both jobs is how "from this date" ends up meaning
            a day the reader still had. */}
        <Milestone
          last
          title={`${formatCreatedOn(endsOn)} · trial ends`}
          detail={`${formatMoney(
            price.taxableAmount
          )} + GST/${term} from ${formatCreatedOn(chargedOn)}.`}
        />
      </ul>

      <Button
        variant="primary"
        size="lg"
        fullWidth
        className="mt-5"
        loading={starting}
        onClick={onStart}
      >
        Activate free trial
      </Button>

      {/* Under the button rather than on it: it is the reassurance that makes
          the press easy, and a button that carried it would be a button with a
          sentence on it. */}
      <p className="mt-3 text-center text-[12px] 3xl:text-[13px] text-ink-subtle">
        No credit card required &middot; Start free, pay after {TRIAL_DAYS} days
      </p>
    </section>
  );
};

export default TrialSummary;
